import { useEffect, useRef, useState } from 'react'
import { parseHpEditInput } from '@/lib/hpEdit'

interface HpEditPopoverProps {
  /** Anchor point in canvas-viewport pixels (the clicked HP pill). The popover
   *  floats just above it, horizontally centered. */
  position: { x: number; y: number }
  current: number
  max: number
  /** Owner-or-GM HP write — same handler the HUD HP bar uses (issue #135). */
  onSubmit: (change: { set: number } | { delta: number }) => void
  onClose: () => void
}

/**
 * On-canvas HP editor — opens when a token's HP bar is clicked, mirroring the
 * selected-token HUD editor so HP can be adjusted in place without selecting
 * the token first. A bare number sets, `+N` / `-N` adjusts (parseHpEditInput).
 * Enter or blur submits; Escape cancels.
 */
export function HpEditPopover({
  position,
  current,
  max,
  onSubmit,
  onClose,
}: HpEditPopoverProps) {
  const [value, setValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const submit = () => {
    const change = parseHpEditInput(value)
    onClose()
    if (change) onSubmit(change)
  }

  return (
    <div
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        transform: 'translate(-50%, calc(-100% - 8px))',
        zIndex: 50,
        pointerEvents: 'auto',
      }}
      // Don't let clicks inside the editor reach the canvas (deselect / pan).
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div
        className="ac-hud-card"
        style={{
          padding: '6px 8px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          alignItems: 'center',
        }}
      >
        <div
          className="font-mono-ac"
          style={{
            fontSize: '0.6875rem',
            color: 'hsl(var(--ac-text-muted))',
            whiteSpace: 'nowrap',
          }}
        >
          HP{' '}
          <b style={{ color: 'hsl(var(--ac-text-light))' }}>{current}</b> / {max}
        </div>
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit()
            else if (e.key === 'Escape') onClose()
          }}
          onBlur={submit}
          placeholder="N, +N, -N"
          inputMode="numeric"
          className="font-mono-ac"
          style={{
            width: 72,
            fontSize: '0.8125rem',
            textAlign: 'center',
            background: 'hsl(var(--ac-dark-accent))',
            color: 'hsl(var(--ac-text-light))',
            border: '1px solid hsl(var(--ac-mint-primary) / 0.5)',
            borderRadius: 4,
            padding: '0 0.25rem',
          }}
        />
      </div>
    </div>
  )
}
